import axios from 'axios';
import * as Routes from '@/modules/routes';

// AIDEV-NOTE: Client half of the Teacher-Paced Mode forensic log (see docs/live-sessions.md,
// "Event log"). Events buffer per session and flush every 2s over HTTP — deliberately not the
// WebSocket, because the events that matter most describe a dead socket. On pagehide the buffer
// goes out via sendBeacon so tab-close/navigation events survive. Plain module (no React) so the
// Zustand channel stores can call it.

export type EventMetadata = Record<string, unknown>;

interface QueuedEvent {
  client_seq: number;
  event_type: string;
  metadata: EventMetadata;
  occurred_at: string;
}

interface PageLeftSnapshot {
  metadata: EventMetadata;
  sessionId: string | null;
}

export const FLUSH_INTERVAL_MS = 5000;
const MAX_BUFFERED = 500;
const MAX_BATCH = 50;

const generateId = (): string => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
);

let pageLoadId = generateId();
let seq = 0;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let listenersAttached = false;
let pageLeftProvider: (() => PageLeftSnapshot) | null = null;
const buffers = new Map<string, QueuedEvent[]>();

const readFlags = (): Record<string, unknown> => {
  try {
    const raw = document.getElementById('slug-attributes')?.dataset.flags;
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

// Absent flag (undefined) means enabled; only an explicit false turns logging off.
const isEnabled = (): boolean => readFlags().live_session_event_logging !== false;

const eventsUrl = (sessionId: string): string => Routes.plato_api_events_live_session_path(sessionId);

const buildBody = (events: QueuedEvent[]) => ({
  events,
  page_load_id: pageLoadId,
  sent_at: new Date().toISOString(),
});

const drain = (sessionId: string): QueuedEvent[][] => {
  const queue = buffers.get(sessionId) ?? [];
  buffers.delete(sessionId);
  const batches: QueuedEvent[][] = [];
  for (let i = 0; i < queue.length; i += MAX_BATCH) {
    batches.push(queue.slice(i, i + MAX_BATCH));
  }
  return batches;
};

const postBatch = (sessionId: string, batch: QueuedEvent[]) => {
  axios.post(eventsUrl(sessionId), buildBody(batch)).catch(() => {});
};

const beaconBatch = (sessionId: string, batch: QueuedEvent[]) => {
  if (typeof navigator.sendBeacon !== 'function') {
    postBatch(sessionId, batch);
    return;
  }
  const payload = new Blob([JSON.stringify(buildBody(batch))], { type: 'application/json' });
  navigator.sendBeacon(eventsUrl(sessionId), payload);
};

const stopTimer = () => {
  if (flushTimer === null) return;
  clearInterval(flushTimer);
  flushTimer = null;
};

export const flushLiveSessionEvents = () => {
  Array.from(buffers.keys()).forEach((sessionId) => {
    drain(sessionId).forEach(batch => postBatch(sessionId, batch));
  });
  stopTimer();
};

const flushWithBeacon = () => {
  Array.from(buffers.keys()).forEach((sessionId) => {
    drain(sessionId).forEach(batch => beaconBatch(sessionId, batch));
  });
  stopTimer();
};

const enqueue = (sessionId: string, eventType: string, metadata: EventMetadata) => {
  const queue = buffers.get(sessionId) ?? [];
  seq += 1;
  queue.push({
    client_seq: seq,
    event_type: eventType,
    metadata,
    occurred_at: new Date().toISOString(),
  });
  if (queue.length > MAX_BUFFERED) queue.splice(0, queue.length - MAX_BUFFERED);
  buffers.set(sessionId, queue);
};

// pagehide is the only reliable "this tab is going away" signal; visibilitychange:hidden also
// fires on tab switches, so it only flushes and never logs page_left.
const handlePageHide = () => {
  const snapshot = pageLeftProvider?.();
  if (snapshot?.sessionId) enqueue(snapshot.sessionId, 'page_left', snapshot.metadata);
  flushWithBeacon();
};

const handleVisibilityChange = () => {
  if (document.visibilityState === 'hidden') flushWithBeacon();
};

const ensureFlushing = () => {
  if (!listenersAttached) {
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    listenersAttached = true;
  }
  if (flushTimer === null) {
    flushTimer = setInterval(flushLiveSessionEvents, FLUSH_INTERVAL_MS);
  }
};

export const logLiveSessionEvent = (
  sessionId: string | null | undefined,
  eventType: string,
  metadata: EventMetadata = {},
) => {
  if (!sessionId || !isEnabled()) return;
  enqueue(sessionId, eventType, metadata);
  ensureFlushing();
};

// The PresentMode forensics hook registers this so page_left can carry the lock / socket state
// at the moment the tab goes away.
export const setPageLeftProvider = (provider: (() => PageLeftSnapshot) | null) => {
  pageLeftProvider = provider;
};

export const getPageLoadId = (): string => pageLoadId;

export const navigationType = (): string | null => {
  try {
    const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    return entry?.type ?? null;
  } catch {
    return null;
  }
};

export const resetLiveSessionEventsForTests = () => {
  buffers.clear();
  stopTimer();
  if (listenersAttached) {
    window.removeEventListener('pagehide', handlePageHide);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    listenersAttached = false;
  }
  pageLeftProvider = null;
  seq = 0;
  pageLoadId = generateId();
};
